import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { PrintQueueItem, PrintRun } from '@accounting/shared';
import {
  useConfirmPrintRun,
  usePendingPrintRuns,
  usePrintChecks,
  usePrintQueue,
  usePrintRun,
  type PrintResult,
} from '../../hooks/useCheckPrinting';
import { ApiError, openFileInNewTab } from '../../lib/api';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/checks/print-queue')({
  component: PrintQueuePage,
});

function errText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return (err.body as { error?: string } | null)?.error ?? fallback;
  }
  return err instanceof Error ? err.message : fallback;
}

function PrintQueuePage() {
  // Undefined means "however many faces the configured stock holds"; the select
  // below only ever shortens a run, since the paper decides the maximum.
  const [override, setOverride] = useState<number | undefined>(undefined);
  const { data: queue, isLoading, error } = usePrintQueue(override);
  const checksPerSheet = queue?.checksPerSheet ?? 3;
  const print = usePrintChecks();
  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [result, setResult] = useState<PrintResult | null>(null);
  const [resumeRunId, setResumeRunId] = useState<string | null>(null);
  const { data: pendingRuns = [] } = usePendingPrintRuns();

  const ready = useMemo(() => queue?.items.filter((i) => i.readyToPrint) ?? [], [queue]);
  const blocked = useMemo(() => queue?.items.filter((i) => !i.readyToPrint) ?? [], [queue]);

  // null selection means "everything ready" — the default, and the common case.
  const isSelected = (id: string) => (selected === null ? true : selected.has(id));
  const selectedIds = ready.filter((i) => isSelected(i.checkId)).map((i) => i.checkId);

  const toggle = (id: string) => {
    const next = new Set(selected ?? ready.map((i) => i.checkId));
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const selectedTotal = ready
    .filter((i) => isSelected(i.checkId))
    .reduce((s, i) => s + parseFloat(i.amount), 0);
  const sheets = Math.ceil(selectedIds.length / checksPerSheet);
  const wasted = sheets * checksPerSheet - selectedIds.length;

  async function doPrint() {
    const res = await print.mutateAsync({
      checkIds: selected === null ? undefined : selectedIds,
      checksPerSheet: override,
    });
    setResult(res);
    setSelected(null);
    // Hand the PDF straight to a new tab so the browser's own print dialog can
    // take it — nothing is written to disk unless the owner saves it.
    window.open(res.objectUrl, '_blank', 'noopener,noreferrer');
  }

  // Either a run just printed, or one the owner came back to finish.
  const activeRunId = result?.runId ?? resumeRunId;
  if (activeRunId) {
    return (
      <ConfirmRunPanel
        runId={activeRunId}
        objectUrl={result?.objectUrl}
        onDone={() => {
          setResult(null);
          setResumeRunId(null);
        }}
      />
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Link to="/checks" style={{ fontSize: 13 }}>← Check register</Link>
        <h1 style={{ fontSize: 24, margin: '8px 0 4px' }}>Print checks</h1>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Every company's queued checks in one batch, so a sheet is never half wasted.
        </p>
      </div>

      {/* Numbers assigned, nothing released. Shown before the queue itself
        * because unfinished paper matters more than the next batch. */}
      {pendingRuns.length > 0 && (
        <div
          className="card"
          style={{ padding: 16, marginBottom: 16, borderLeft: '3px solid var(--warn)' }}
        >
          <h2 style={{ fontSize: 15, marginTop: 0 }}>
            {pendingRuns.length} print run{pendingRuns.length === 1 ? '' : 's'} not finished
          </h2>
          <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
            These checks have numbers on them but have not been released. Finish them before
            printing more, or the register will not match the paper.
          </p>
          <table className="table">
            <tbody>
              {pendingRuns.map((r) => (
                <tr key={r.id}>
                  <td>{new Date(r.createdAt).toLocaleString()}</td>
                  <td>
                    {r.checkCount} check{r.checkCount === 1 ? '' : 's'} · {r.sheetCount} sheet
                    {r.sheetCount === 1 ? '' : 's'}
                  </td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(r.totalAmount)}</td>
                  <td className="muted">
                    {r.checks[0]?.checkNumber}
                    {r.checks.length > 1 ? `–${r.checks[r.checks.length - 1]?.checkNumber}` : ''}
                  </td>
                  <td style={{ width: 1 }}>
                    <button className="btn-primary" onClick={() => setResumeRunId(r.id)}>
                      Finish
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load the print queue.</p>}

      {queue && queue.totalCount === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>Nothing is waiting to print.</p>
          <Link to="/checks/new" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
            Write a check
          </Link>
        </div>
      )}

      {queue && queue.totalCount > 0 && (
        <>
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <Stat label="Selected" value={String(selectedIds.length)} sub={`of ${ready.length} ready`} />
              <Stat label="Total" value={formatCurrency(selectedTotal.toFixed(2))} />
              <Stat label="Sheets" value={String(sheets)} sub={`${checksPerSheet} per sheet`} />
              <Stat
                label="Wasted faces"
                value={String(wasted)}
                sub={wasted > 0 ? 'shredded on the last sheet' : 'perfect fit'}
                tone={wasted > 0 ? 'warn' : 'ok'}
              />
              <label style={{ fontSize: 13 }}>
                Checks per sheet{' '}
                <select
                  value={override ?? 0}
                  onChange={(e) => setOverride(Number(e.target.value) || undefined)}
                >
                  <option value={0}>{checksPerSheet} (stock)</option>
                  {Array.from({ length: Math.max(0, checksPerSheet - 1) }, (_, i) => i + 1).map(
                    (n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ),
                  )}
                </select>
              </label>
              <button
                className="btn-primary"
                style={{ marginLeft: 'auto' }}
                disabled={selectedIds.length === 0 || print.isPending}
                onClick={doPrint}
              >
                {print.isPending
                  ? 'Numbering…'
                  : `Print ${selectedIds.length} check${selectedIds.length === 1 ? '' : 's'} (${sheets} sheet${sheets === 1 ? '' : 's'})`}
              </button>
            </div>
            {wasted > 0 && selectedIds.length > 0 && (
              <p className="muted" style={{ fontSize: 12, margin: '12px 0 0' }}>
                {wasted} check face{wasted === 1 ? '' : 's'} on the last sheet will print blank.
                Waiting for {wasted} more queued check{wasted === 1 ? '' : 's'} would fill it.
              </p>
            )}
            {print.error && (
              <p className="field-error" style={{ marginBottom: 0, marginTop: 12 }}>
                {errText(print.error, 'Printing failed.')}
              </p>
            )}
          </div>

          {blocked.length > 0 && (
            <div className="card" style={{ padding: 16, marginBottom: 16, borderLeft: '3px solid var(--warn)' }}>
              <h2 style={{ fontSize: 15, marginTop: 0 }}>
                {blocked.length} check{blocked.length === 1 ? '' : 's'} cannot print yet
              </h2>
              <table className="table">
                <tbody>
                  {blocked.map((i) => (
                    <tr key={i.checkId}>
                      <td>{i.companyName}</td>
                      <td>{i.payeeName}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(i.amount)}</td>
                      <td className="muted">{i.blockedReason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
                Finish the account setup under{' '}
                <Link to="/settings/check-printing">Settings → Check printing</Link>.
              </p>
            </div>
          )}

          <QueueTable
            items={ready}
            isSelected={isSelected}
            onToggle={toggle}
            onSelectAll={() => setSelected(null)}
            onSelectNone={() => setSelected(new Set())}
          />
        </>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'ok' | 'warn';
}) {
  const color = tone === 'warn' ? 'var(--warn)' : tone === 'ok' ? 'var(--ok)' : undefined;
  return (
    <div>
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 11 }}>{sub}</div>}
    </div>
  );
}

/** The queue, grouped by company so a mixed batch is still readable. */
function QueueTable({
  items,
  isSelected,
  onToggle,
  onSelectAll,
  onSelectNone,
}: {
  items: PrintQueueItem[];
  isSelected: (id: string) => boolean;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
}) {
  const groups = useMemo(() => {
    const byCompany = new Map<string, PrintQueueItem[]>();
    for (const i of items) {
      if (!byCompany.has(i.companyName)) byCompany.set(i.companyName, []);
      byCompany.get(i.companyName)!.push(i);
    }
    return [...byCompany.entries()];
  }, [items]);

  if (items.length === 0) return null;

  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center' }}>
        <h2 style={{ fontSize: 15, margin: 0, marginRight: 'auto' }}>Ready to print</h2>
        <button className="btn-secondary" onClick={onSelectAll}>Select all</button>
        <button className="btn-secondary" onClick={onSelectNone}>Select none</button>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 1 }}></th>
            <th>Date</th>
            <th>Payee</th>
            <th>Account</th>
            <th>Memo</th>
            <th style={{ textAlign: 'right' }}>Amount</th>
            <th style={{ textAlign: 'right' }}>Check no.</th>
          </tr>
        </thead>
        {groups.map(([companyName, rows]) => (
          <tbody key={companyName}>
            <tr>
              <td colSpan={7} style={{ paddingTop: 14, fontWeight: 700, fontSize: 13 }}>
                {companyName}
                <span className="muted" style={{ fontWeight: 400, marginLeft: 8 }}>
                  {rows.length} check{rows.length === 1 ? '' : 's'} ·{' '}
                  {formatCurrency(
                    rows.reduce((s, r) => s + parseFloat(r.amount), 0).toFixed(2),
                  )}
                </span>
              </td>
            </tr>
            {rows.map((i) => (
              <tr key={i.checkId}>
                <td>
                  <input
                    type="checkbox"
                    checked={isSelected(i.checkId)}
                    onChange={() => onToggle(i.checkId)}
                    aria-label={`Include check to ${i.payeeName}`}
                  />
                </td>
                <td>{formatAccountingDate(i.checkDate)}</td>
                <td>
                  <Link to="/checks/$id" params={{ id: i.checkId }}>{i.payeeName}</Link>
                </td>
                <td className="muted">{i.bankAccountCode} — {i.bankAccountName}</td>
                <td className="muted">{i.memo ?? ''}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(i.amount)}</td>
                <td style={{ textAlign: 'right' }} className="muted">
                  {i.projectedCheckNumber}
                </td>
              </tr>
            ))}
          </tbody>
        ))}
      </table>
      <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
        Check numbers shown are what each check would get. They are assigned for real when you print.
      </p>
    </div>
  );
}

/**
 * The step that turns paper into a payment.
 *
 * Nothing becomes an outstanding check until the owner says the sheets came out
 * clean. Anything marked spoiled has its number recorded as consumed — so the
 * register can explain the gap — and goes back to the queue for a fresh number.
 */
function ConfirmRunPanel({
  runId,
  objectUrl,
  onDone,
}: {
  runId: string;
  /** Absent when recovering an earlier run — the PDF is re-fetched instead. */
  objectUrl?: string;
  onDone: () => void;
}) {
  const { data: run, isLoading } = usePrintRun(runId);
  const confirm = useConfirmPrintRun();
  const [spoiled, setSpoiled] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const [done, setDone] = useState<PrintRun | null>(null);

  const toggle = (id: string) => {
    const next = new Set(spoiled);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSpoiled(next);
  };

  if (done) {
    const released = done.checkCount - spoiled.size;
    return (
      <div className="card" style={{ padding: 24, maxWidth: 640 }}>
        <h1 style={{ fontSize: 20, marginTop: 0 }}>Run closed</h1>
        <p>
          {done.status === 'abandoned'
            ? 'Every check in this run was spoiled. All of them are back in the queue, waiting for fresh numbers.'
            : `${released} check${released === 1 ? '' : 's'} released as outstanding.`}
          {spoiled.size > 0 &&
            ` ${spoiled.size} number${spoiled.size === 1 ? '' : 's'} recorded as spoiled; those checks are back in the queue.`}
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/checks" className="btn-primary" style={{ textDecoration: 'none' }}>
            Check register
          </Link>
          <button className="btn-secondary" onClick={onDone}>Back to queue</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ fontSize: 24, margin: '0 0 4px' }}>Did the checks print correctly?</h1>
      <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
        {run?.checkCount ?? 0} check{run?.checkCount === 1 ? '' : 's'} on {run?.sheetCount ?? 0}{' '}
        sheet{run?.sheetCount === 1 ? '' : 's'}. Numbers are already assigned — check the paper
        before confirming.
      </p>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {objectUrl ? (
            <>
              <a
                className="btn-secondary"
                href={objectUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
              >
                Open the PDF again
              </a>
              <a
                className="btn-secondary"
                href={objectUrl}
                download={`checks-${runId.slice(0, 8)}.pdf`}
                style={{ textDecoration: 'none' }}
              >
                Download PDF
              </a>
            </>
          ) : (
            // Recovered run: the PDF is regenerated from the stored rows, so it
            // reproduces exactly the same numbers that are already on paper.
            <button
              className="btn-secondary"
              onClick={() => openFileInNewTab(`/check-printing/runs/${runId}/pdf`)}
            >
              Open the PDF
            </button>
          )}
          <span className="muted" style={{ fontSize: 12 }}>
            Load the check stock, then print at 100% scale — any "fit to page" setting will shift
            the MICR line out of tolerance.
          </span>
        </div>
      </div>

      {isLoading && <p className="muted">Loading the run…</p>}

      {run && (
        <div className="card" style={{ padding: 16 }}>
          <h2 style={{ fontSize: 15, marginTop: 0 }}>
            Tick anything that came out wrong — jammed, smeared, or misaligned
          </h2>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 1 }}>Spoiled</th>
                <th>Sheet</th>
                <th>Check no.</th>
                <th>Company</th>
                <th>Payee</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {run.checks.map((c) => (
                <tr key={c.checkId} style={{ opacity: spoiled.has(c.checkId) ? 0.55 : 1 }}>
                  <td>
                    <input
                      type="checkbox"
                      checked={spoiled.has(c.checkId)}
                      onChange={() => toggle(c.checkId)}
                      aria-label={`Mark check ${c.checkNumber} spoiled`}
                    />
                  </td>
                  <td className="muted">
                    {c.sheetIndex}, pos {c.positionOnSheet}
                  </td>
                  <td><strong>{c.checkNumber}</strong></td>
                  <td>{c.companyName}</td>
                  <td>{c.payeeName}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(c.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {spoiled.size > 0 && (
            <label style={{ display: 'block', fontSize: 13, margin: '12px 0' }}>
              What went wrong?
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Printer jammed on the second sheet"
                style={{ display: 'block', width: '100%', maxWidth: 480, marginTop: 4 }}
              />
            </label>
          )}

          {confirm.error && (
            <p className="field-error">{errText(confirm.error, 'Could not close the run.')}</p>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              className="btn-primary"
              disabled={confirm.isPending}
              onClick={async () => {
                const closed = await confirm.mutateAsync({
                  runId,
                  spoiledCheckIds: [...spoiled],
                  spoilReason: reason || undefined,
                });
                setDone(closed);
              }}
            >
              {spoiled.size === 0
                ? 'All printed correctly'
                : `Confirm — ${spoiled.size} spoiled, reprint later`}
            </button>
          </div>
          <p className="muted" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
            A spoiled number is never reused. It is recorded so the gap in the register has an
            explanation, and the payment goes back in the queue for a new number.
          </p>
        </div>
      )}
    </div>
  );
}
